import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Dropdown from "@/components/ui/dropdown";
import { Category as CategoryIcon } from "@/components/icons";
import type { Category } from "../types/categories.types";
import type { CreateCategoryPayload } from "../types/categories.types";
import styles from "./CategoryModal.module.scss";

const categoryFormSchema = z.object({
  name: z
    .string()
    .min(2, "categoriesPage.validation.nameMin")
    .max(100, "categoriesPage.validation.nameMax"),
  parentId: z.union([z.literal(""), z.string().uuid()]),
  description: z.string().max(500, "categoriesPage.validation.descriptionMax"),
  isActive: z.boolean(),
});

export interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateCategoryPayload) => void | Promise<void>;
  rootCategories: Category[];
  onSearchParent?: (search: string) => void;
  editCategory?: Category | null;
  title?: string;
  /**
   * Sunucudan dönen ve Kategori Adı alanına ait olan hata (ör. aynı üst kategori
   * altında mükerrer isim → 409). Toast yerine alanın altında inline gösterilir.
   */
  submitError?: string | null;
  /** Kullanıcı ismi düzeltmeye başlayınca sunucu hatasını temizlemek için. */
  onClearSubmitError?: () => void;
}

const CategoryModal: React.FC<CategoryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  rootCategories,
  onSearchParent,
  editCategory = null,
  title,
  submitError = null,
  onClearSubmitError,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [parentValue, setParentValue] = useState<string>("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /**
   * Üst kategori seçenekleri.
   *
   * - Liste TÜM kategorileri içerir (yalnız kökler değil) — böylece alt kategorinin
   *   altına da kategori eklenebilir, ağaç çok seviyeli olur.
   * - Düzenlemede kategorinin KENDİSİ ve tüm ALT AĞACI elenir; aksi halde kategori
   *   kendi torununun altına taşınıp köke bağlanmayan bir halka oluşturulabiliyor
   *   (backend de bunu reddeder, burada seçenek hiç gösterilmez).
   * - Etiket tam yol olarak yazılır ("Merkez / Online"): aynı isim farklı üst
   *   kategoriler altında serbest olduğu için düz isim ayırt edici değil.
   */
  const parentOptions = useMemo(() => {
    const byId = new Map(rootCategories.map((c) => [c.id, c]));

    const buildPath = (c: Category): string => {
      const parts: string[] = [c.name];
      const seen = new Set<string>([c.id]);
      let cur = c.parentId ? byId.get(c.parentId) : undefined;
      while (cur && !seen.has(cur.id)) {
        seen.add(cur.id);
        parts.unshift(cur.name);
        cur = cur.parentId ? byId.get(cur.parentId) : undefined;
      }
      return parts.join(" / ");
    };

    // editCategory'nin alt ağacı (kendisi dahil)
    const excluded = new Set<string>();
    if (editCategory?.id) {
      excluded.add(editCategory.id);
      let grew = true;
      while (grew) {
        grew = false;
        for (const c of rootCategories) {
          if (!excluded.has(c.id) && c.parentId && excluded.has(c.parentId)) {
            excluded.add(c.id);
            grew = true;
          }
        }
      }
    }

    return [
      { label: t("categories.noParentOption"), value: "" },
      ...rootCategories
        .filter((c) => !excluded.has(c.id))
        .map((c) => ({ label: buildPath(c), value: c.id }))
        .sort((a, b) => a.label.localeCompare(b.label, "tr")),
    ];
  }, [rootCategories, editCategory?.id, t]);

  // Zod doğrulaması yerelde, submitError sunucudan gelir; ikisi de aynı alanın
  // altında gösterilir. Yerel doğrulama hatası önceliklidir.
  const nameError = fieldErrors.name || submitError || "";

  const selectedParentLabel =
    parentValue === ""
      ? (parentOptions[0]?.label ?? "")
      : (parentOptions.find((o) => o.value === parentValue)?.label ?? "");

  const validateForm = () => {
    const result = categoryFormSchema.safeParse({
      name: name.trim(),
      parentId: parentValue,
      description,
      isActive,
    });
    if (result.success) {
      setFieldErrors({});
      return true;
    }
    const flattened = result.error.flatten().fieldErrors;
    const errors: Record<string, string> = {};
    if (flattened.name?.[0]) errors.name = flattened.name[0];
    if (flattened.parentId?.[0]) errors.parentId = flattened.parentId[0];
    if (flattened.description?.[0])
      errors.description = flattened.description[0];
    setFieldErrors(errors);
    return false;
  };

  useEffect(() => {
    const result = categoryFormSchema.safeParse({
      name: name.trim(),
      parentId: parentValue,
      description,
      isActive,
    });
    setIsFormValid(result.success);
  }, [name, parentValue, description, isActive]);

  useEffect(() => {
    if (isOpen) {
      setFieldErrors({});
      if (editCategory) {
        setName(editCategory.name ?? "");
        setParentValue(editCategory.parentId ?? "");
        setDescription(editCategory.description ?? "");
        setIsActive(editCategory.isActive ?? true);
      } else {
        setName("");
        setParentValue("");
        setDescription("");
        setIsActive(true);
      }
    }
  }, [isOpen, editCategory]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting || !validateForm()) return;
    const isEditing = !!editCategory;
    const parentId = parentValue === "" ? null : (parentValue as string);
    const trimmedDesc = description.trim();
    const payload: CreateCategoryPayload = {
      name: name.trim(),
      parentId: isEditing ? parentId : (parentId ?? undefined),
      description: editCategory ? trimmedDesc : trimmedDesc || undefined,
      isActive,
    };
    // Modal'ı burada KAPATMA: kayıt başarısız olursa (örn. aynı üst kategori
    // altında mükerrer isim) girilen değerler korunmalı. Başarılı olduğunda
    // parent onSubmit içinde modal'ı kapatıyor.
    setIsSubmitting(true);
    try {
      await onSubmit(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusCards: {
    value: boolean;
    label: string;
    description: string;
    className: string;
  }[] = [
    {
      value: true,
      label: t("categories.statusActive"),
      description: t("categories.statusActiveHint"),
      className: styles.statusCardActive,
    },
    {
      value: false,
      label: t("categories.statusPassive"),
      description: t("categories.statusPassiveHint"),
      className: styles.statusCardPassive,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title ?? t("categories.addTitle")}
      size="lg"
      withForm
      formId="categoryForm"
      handleSubmit={handleSubmit}
      isFormValid={isFormValid && !isSubmitting}
      submitButtonText={t("buttons.save")}
    >
      <div className={styles.form}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>1</span>
            <div>
              <h4 className={styles.sectionTitle}>
                {t("categories.sectionBasic")}
              </h4>
              <p className={styles.sectionSubtitle}>
                {t("categories.sectionBasicHint")}
              </p>
            </div>
          </div>
          <div className={styles.grid2}>
            <Input
              label={t("categories.name")}
              placeholder={t("categories.namePlaceholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (fieldErrors.name)
                  setFieldErrors((prev) => ({ ...prev, name: "" }));
                if (submitError) onClearSubmitError?.();
              }}
              error={!!nameError}
              errorMessage={nameError}
              maxLength={100}
              required
            />
            <Dropdown
              label={t("categories.parent")}
              placeholder={t("categories.selectParent")}
              options={parentOptions}
              value={parentValue}
              selectedLabel={selectedParentLabel}
              onSelect={(value) => {
                setParentValue(Array.isArray(value) ? (value[0] ?? "") : value);
                if (fieldErrors.parentId)
                  setFieldErrors((prev) => ({ ...prev, parentId: "" }));
                // Mükerrer isim hatası (isim + üst kategori) çiftine bağlı;
                // üst kategori değişince de geçersizleşir.
                if (submitError) onClearSubmitError?.();
              }}
              onSearch={onSearchParent}
              searchable
              allowClear
              error={!!fieldErrors.parentId}
              errorMessage={fieldErrors.parentId}
            />
          </div>
          {parentOptions.length === 1 && (
            <p className={styles.inlineHint}>
              {t("categories.noParentAvailable")}
            </p>
          )}
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>2</span>
            <div>
              <h4 className={styles.sectionTitle}>
                {t("categories.sectionDetails")}
              </h4>
              <p className={styles.sectionSubtitle}>
                {t("categories.sectionDetailsHint")}
              </p>
            </div>
          </div>
          <Textarea
            label={t("categories.description")}
            placeholder={t("categories.descriptionPlaceholder")}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (fieldErrors.description)
                setFieldErrors((prev) => ({ ...prev, description: "" }));
            }}
            error={!!fieldErrors.description}
            errorMessage={fieldErrors.description}
            maxLength={500}
            rows={3}
          />
          <div className={styles.statusCards}>
            {statusCards.map((card) => {
              const active = isActive === card.value;
              return (
                <button
                  key={String(card.value)}
                  type="button"
                  className={`${styles.statusCard} ${card.className} ${active ? styles.statusCardSelected : ""}`}
                  onClick={() => setIsActive(card.value)}
                  aria-pressed={active}
                >
                  <span className={styles.statusCardIcon}>
                    <CategoryIcon width={18} height={18} />
                  </span>
                  <span className={styles.statusCardBody}>
                    <span className={styles.statusCardLabel}>{card.label}</span>
                    <span className={styles.statusCardDescription}>
                      {card.description}
                    </span>
                  </span>
                  <span className={styles.statusCardRadio} aria-hidden>
                    <span className={styles.statusCardRadioInner} />
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default CategoryModal;
